/**
 * Region collectors built on the shared camera-independent `walkPointCloud`.
 *
 * Two shapes, matching the two detection phases:
 *   - `collectBox`      — small fine-LOD cube around the seed click (good
 *                          cross-section density → reliable radius).
 *   - `collectCorridor` — long thin coarse-LOD tube along the fitted axis
 *                          (reach for extent, not density).
 *
 * Both stream world-space XYZ into a growable `Float64Array` and enforce a hard
 * point cap. The cap is honoured by throwing a sentinel out of `onPoint` (the
 * walker has no return-value early-stop), which we catch and treat as a clean
 * "collected enough" stop. Mirrors the `maxRawPoints` trick in the elevation
 * profile sampler.
 */

import { PointCloudWalkAbortError, walkPointCloud, type WalkPhase } from '@/lib/pointcloud/walkPointCloud';
import type { PointCloudOctree } from '@pnext/three-loader';
import { axialCoord, radialDistance } from './fitCylinder';
import type { Vec3 } from './types';

class PointCapReached extends Error {
  constructor() {
    super('point cap reached');
    this.name = 'PointCapReached';
  }
}

class GrowableXYZ {
  buf: Float64Array;
  count = 0;
  constructor(initial = 1 << 16) {
    this.buf = new Float64Array(initial * 3);
  }
  push(x: number, y: number, z: number): void {
    if (this.count * 3 + 3 > this.buf.length) {
      const next = new Float64Array(this.buf.length * 2);
      next.set(this.buf);
      this.buf = next;
    }
    const o = this.count * 3;
    this.buf[o] = x;
    this.buf[o + 1] = y;
    this.buf[o + 2] = z;
    this.count++;
  }
}

export interface CollectResult {
  positions: Float64Array;
  count: number;
  nodesUsed: number;
  capped: boolean;
}

type ProgressFn = (phase: WalkPhase, done: number, total: number) => void;

/** Collect points inside an axis-aligned cube centred on `center`. */
export async function collectBox(
  pco: PointCloudOctree,
  center: Vec3,
  halfSize: number,
  targetSpacing: number | undefined,
  maxPoints: number,
  onProgress?: ProgressFn,
  signal?: AbortSignal,
): Promise<CollectResult> {
  const minX = center[0] - halfSize;
  const maxX = center[0] + halfSize;
  const minY = center[1] - halfSize;
  const maxY = center[1] + halfSize;
  const minZ = center[2] - halfSize;
  const maxZ = center[2] + halfSize;
  const out = new GrowableXYZ();
  let capped = false;

  try {
    const r = await walkPointCloud({
      pco,
      targetSpacing,
      onProgress,
      signal,
      shouldVisitNode: (b) =>
        b.max.x >= minX && b.min.x <= maxX && b.max.y >= minY && b.min.y <= maxY && b.max.z >= minZ && b.min.z <= maxZ,
      onPoint: (x, y, z) => {
        if (x < minX || x > maxX || y < minY || y > maxY || z < minZ || z > maxZ) return;
        out.push(x, y, z);
        if (out.count >= maxPoints) {
          capped = true;
          throw new PointCapReached();
        }
      },
    });
    return { positions: out.buf, count: out.count, nodesUsed: r.nodesUsed, capped };
  } catch (e) {
    if (e instanceof PointCapReached) return { positions: out.buf, count: out.count, nodesUsed: 0, capped: true };
    throw e;
  }
}

/**
 * Collect points inside a tube of `outerRadius` around the line through
 * `axisPoint` along unit `axis`, within ±`halfLength` of `axisPoint`.
 */
export async function collectCorridor(
  pco: PointCloudOctree,
  axisPoint: Vec3,
  axis: Vec3,
  halfLength: number,
  outerRadius: number,
  targetSpacing: number | undefined,
  maxPoints: number,
  onProgress?: ProgressFn,
  signal?: AbortSignal,
): Promise<CollectResult> {
  // Corridor AABB: the segment endpoints expanded by outerRadius.
  const aStart: Vec3 = [
    axisPoint[0] - axis[0] * halfLength,
    axisPoint[1] - axis[1] * halfLength,
    axisPoint[2] - axis[2] * halfLength,
  ];
  const aEnd: Vec3 = [
    axisPoint[0] + axis[0] * halfLength,
    axisPoint[1] + axis[1] * halfLength,
    axisPoint[2] + axis[2] * halfLength,
  ];
  const minX = Math.min(aStart[0], aEnd[0]) - outerRadius;
  const maxX = Math.max(aStart[0], aEnd[0]) + outerRadius;
  const minY = Math.min(aStart[1], aEnd[1]) - outerRadius;
  const maxY = Math.max(aStart[1], aEnd[1]) + outerRadius;
  const minZ = Math.min(aStart[2], aEnd[2]) - outerRadius;
  const maxZ = Math.max(aStart[2], aEnd[2]) + outerRadius;

  const out = new GrowableXYZ();
  let capped = false;

  try {
    const r = await walkPointCloud({
      pco,
      targetSpacing,
      onProgress,
      signal,
      shouldVisitNode: (b) =>
        b.max.x >= minX && b.min.x <= maxX && b.max.y >= minY && b.min.y <= maxY && b.max.z >= minZ && b.min.z <= maxZ,
      onPoint: (x, y, z) => {
        const p: Vec3 = [x, y, z];
        if (Math.abs(axialCoord(p, axisPoint, axis)) > halfLength) return;
        if (radialDistance(p, axisPoint, axis) > outerRadius) return;
        out.push(x, y, z);
        if (out.count >= maxPoints) {
          capped = true;
          throw new PointCapReached();
        }
      },
    });
    return { positions: out.buf, count: out.count, nodesUsed: r.nodesUsed, capped };
  } catch (e) {
    if (e instanceof PointCapReached) return { positions: out.buf, count: out.count, nodesUsed: 0, capped: true };
    throw e;
  }
}

export { PointCloudWalkAbortError };
